import { computed, inject } from '@angular/core';
import { ApiError } from '@bizzdesign/bizzdesign-sdk';
import {
  type GenericObject,
  type NeighboursDefinition,
} from '@bizzdesign/modeler/models';
import { contextMenuUiEvents } from '@bizzdesign/modeler/utils-canvazz/events';
import { ArchiMateMetaModelDefinition } from '@bizzdesign/sdk-knowledgegraph/archimate';
import { BDCoreMetaModelDefinition } from '@bizzdesign/sdk-knowledgegraph/bdcore';
import type {
  DeletedElementsState,
  ObjectSearchResultDto,
  RepositoryLoadedElementData,
  RepositoryLoadedElementIdPair,
} from '@bizzdesign/shared/models';
import {
  getBDCoreObjectTypeIcon,
  getObjectTypeIcon,
} from '@bizzdesign/shared/utils';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { Dispatcher } from '@ngrx/signals/events';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { type MenuItem } from 'primeng/api';
import { distinctUntilChanged, filter, from, pipe, switchMap, tap } from 'rxjs';
import { fromRepoStatusUiEvents } from '../../events/frame/from-repo-status.event';
import {
  GET_OBJECTS_PAGE_SIZE,
  ModelerDataService,
} from '../../services/modeler-data.service';
import { ModelerLocalStore } from '../modeler/modeler.store';
import { objectSearchConfig } from './repository.store.utils';

export type RepositoryNeighborsState = {
  isLoading: boolean;
  loadableCount: number;
  data: NeighboursDefinition | null;
  error: Error | null;
};

type RepositoryState = {
  results: ObjectSearchResultDto[];
  selectedRepositoryItemId: string;
  loadedElementsIds: RepositoryLoadedElementIdPair[];
  loadedElementsData: RepositoryLoadedElementData[];
  deletedElements: DeletedElementsState;
  /**
   * Represents the state related to the neighbors of the selected element from the repository.
   * It stores the loading state, the total number of neighbors available, and the response from API for neighbors data.
   */
  neighbors: RepositoryNeighborsState;
};

const initialState: RepositoryState = {
  results: [],
  loadedElementsIds: [],
  loadedElementsData: [],
  deletedElements: {
    ids: undefined,
    error: null,
  },
  selectedRepositoryItemId: '',
  neighbors: {
    data: null,
    error: null,
    isLoading: false,
    loadableCount: 0,
  },
};

function iconForRepositorySearchObjectType(objectType: string): string {
  if (BDCoreMetaModelDefinition.isObjectType(objectType)) {
    return getBDCoreObjectTypeIcon(objectType);
  }
  if (ArchiMateMetaModelDefinition.isObjectType(objectType)) {
    return getObjectTypeIcon(objectType);
  }
  throw new ApiError(`Unknown object type: ${objectType}`);
}

export const RepositoryLocalStore = signalStore(
  { protectedState: false },
  withState(initialState),
  withProps(() => ({
    modelerStore: inject(ModelerLocalStore),
    dataService: inject(ModelerDataService),
    dispatcher: inject(Dispatcher),
  })),
  withComputed(
    ({
      results,
      selectedRepositoryItemId,
      loadedElementsIds,
      modelerStore,
    }) => {
      const searchMetamodelScope = computed(() => {
        const metamodelName =
          modelerStore.metamodelMetadata().currentMetamodelName;
        if (!metamodelName || !objectSearchConfig[metamodelName]) {
          return [];
        }
        return objectSearchConfig[metamodelName];
      });

      return {
        searchMetamodelScope,
        repositoryMenuItems: computed(() =>
          results().map(
            (searchResult) =>
              ({
                key: searchResult['type'],
                icon: iconForRepositorySearchObjectType(searchResult['type']),
                label: searchResult['name'],
                id: searchResult['id'],
                metadata: {
                  external: {
                    id: searchResult['externalId'],
                    source: searchResult['externalSource'],
                  },
                },
              }) as MenuItem,
          ),
        ),
        selectedElementFromRepo: computed(() =>
          results().find((res) => res.id === selectedRepositoryItemId()),
        ),
        loadedElementsSemanticIds: computed(() =>
          loadedElementsIds().map((ids) => ids.semanticId),
        ),
      };
    },
  ),
  withMethods((store) => {
    const loadFromRepoElementsByTerm = rxMethod<string>(
      pipe(
        distinctUntilChanged(),
        tap(() => {
          patchState(store, {
            results: [],
          });
        }),
        filter((term) => !!term),
        switchMap((term) =>
          store.dataService
            .searchInRepo$(term, store.searchMetamodelScope())
            .pipe(
              tapResponse({
                next: (searchResults) => {
                  patchState(store, {
                    results: [...searchResults],
                  });
                },
                error: () => {
                  patchState(store, {
                    results: [],
                  });
                },
              }),
            ),
        ),
      ),
    );
    const checkNeighborsOfElement = rxMethod<string>(
      pipe(
        tap(() =>
          patchState(store, {
            neighbors: { ...initialState.neighbors, isLoading: true },
          }),
        ),
        switchMap((semanticsId) =>
          from(store.dataService.getNeighbours(semanticsId)).pipe(
            tapResponse({
              next: (data) => {
                const loadableCount = data.links.filter(
                  (l) => !store.loadedElementsSemanticIds().includes(l.id),
                ).length;
                patchState(store, {
                  neighbors: {
                    ...store.neighbors(),
                    data,
                    isLoading: false,
                    loadableCount,
                  },
                });
                store.dispatcher.dispatch(
                  contextMenuUiEvents.relatedObjectsChecked(loadableCount),
                );
              },
              error: (error: Error) => {
                patchState(store, {
                  neighbors: {
                    ...store.neighbors(),
                    error,
                    isLoading: false,
                  },
                });
              },
            }),
          ),
        ),
      ),
    );
    const setSelectedRepositoryItemId = (selectedRepositoryItemId: string) => {
      patchState(store, {
        selectedRepositoryItemId,
      });
    };
    const setLoadedElementsIds = (
      loadedElementsIds: RepositoryLoadedElementIdPair[],
    ) => {
      patchState(store, {
        loadedElementsIds,
      });
    };
    const setLoadedElementsData = (
      loadedElementsData: RepositoryLoadedElementData[],
    ) => {
      patchState(store, {
        loadedElementsData,
      });
    };
    const checkForDeletedElements = (showLoading = false) => {
      const repositoryObjects = store.loadedElementsSemanticIds();
      const showLoadingIndicator =
        showLoading && repositoryObjects.length > GET_OBJECTS_PAGE_SIZE;
      if (showLoadingIndicator) {
        patchState(store, (state) => ({
          deletedElements: {
            ...state.deletedElements,
            isLoading: true,
          },
        }));
      }
      store.dataService
        .getDeletedObjects(store.loadedElementsIds())
        .then((deletedElements) => {
          if (deletedElements.error) {
            patchState(store, (state) => ({
              deletedElements: {
                ...state.deletedElements,
                error: deletedElements.error,
                isLoading: false,
              },
            }));
            return;
          }

          patchState(store, {
            deletedElements: {
              ...deletedElements,
              isLoading: false,
            },
          });
          const nonDeletedElements = store
            .loadedElementsData()
            .filter(
              (value) =>
                !deletedElements.ids?.find(
                  (e) => e.semanticId === value.semanticId,
                ),
            );
          if (!nonDeletedElements.length) return;
          store.dispatcher.dispatch(
            fromRepoStatusUiEvents.startedCheckStatus({
              data: nonDeletedElements,
            }),
          );
        })
        .catch(() => {
          patchState(store, (state) => ({
            deletedElements: {
              ...state.deletedElements,
              error: new Error('Failed to fetch deleted objects'),
              isLoading: false,
            },
          }));
          const elements = store.loadedElementsData();
          if (!elements.length) return;
          store.dispatcher.dispatch(
            fromRepoStatusUiEvents.startedCheckStatus({ data: elements }),
          );
        });
    };

    const resetLoadedNeighbors = rxMethod<GenericObject>(
      pipe(
        distinctUntilChanged((a, b) => a?.['id'] === b?.['id']),
        tap(() => {
          patchState(store, {
            neighbors: { ...initialState.neighbors },
          });
        }),
      ),
    );
    const addDeletedElement = (semanticId: string) => {
      const existingIds = store.deletedElements().ids ?? [];
      if (existingIds.some((pair) => pair.semanticId === semanticId)) return;

      const matchingPair = store
        .loadedElementsIds()
        .find((pair) => pair.semanticId === semanticId);
      if (!matchingPair) return;

      patchState(store, (state) => ({
        deletedElements: {
          ...state.deletedElements,
          ids: [...existingIds, matchingPair],
        },
      }));
    };
    const resetValues = () => {
      patchState(store, {
        ...initialState,
      });
    };
    return {
      loadFromRepoElementsByTerm,
      setSelectedRepositoryItemId,
      setLoadedElementsIds,
      setLoadedElementsData,
      checkForDeletedElements,
      checkNeighborsOfElement,
      resetLoadedNeighbors,
      addDeletedElement,
      resetValues,
    };
  }),
);

export type RepositoryLocalStore = InstanceType<typeof RepositoryLocalStore>;
